import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { motion, AnimatePresence } from "framer-motion";
import Confetti from "react-confetti";
import {
  Sparkles,
  Flame,
  TrendingUp,
  FileText,
  Settings,
  Upload,
  File,
  Check,
  Star,
  Camera,
  Send,
  Clock,
  Target,
  Apple,
  Beef,
  Wheat,
  Droplet,
  ChevronLeft,
  ChevronRight,
  Edit,
  Save,
  Plus,
  X,
  CheckCircle,
  LogOut
} from "lucide-react";

// Simulated meal plan data
const defaultMealPlan = {
  calories: 2200,
  protein: 150,
  carbs: 220,
  fat: 70,
  meals: {
    desayuno: {
      name: "Desayuno",
      foods: ["Avena con frutas", "Claras de huevo", "Jugo natural"],
      calories: 500,
      protein: 35,
      carbs: 55,
      fat: 12,
      time: "07:00 - 09:00"
    },
    almuerzo: {
      name: "Almuerzo",
      foods: ["Pechuga de pollo", "Arroz integral", "Vegetales"],
      calories: 700,
      protein: 50,
      carbs: 70,
      fat: 18,
      time: "12:00 - 14:00"
    },
    cena: {
      name: "Cena",
      foods: ["Salmón", "Quinoa", "Ensalada verde"],
      calories: 600,
      protein: 45,
      carbs: 45,
      fat: 25,
      time: "19:00 - 21:00"
    },
    snacks: {
      name: "Snacks",
      foods: ["Proteína whey", "Frutos secos", "Banana"],
      calories: 400,
      protein: 20,
      carbs: 50,
      fat: 15,
      time: "Durante el día"
    }
  }
};

const CircularProgress = ({ value, max, color, size = 70, strokeWidth = 6, icon: Icon }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const progress = Math.min(value / max, 1);
  const strokeDashoffset = circumference - progress * circumference;
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#E5E7EB" strokeWidth={strokeWidth} />
          <circle
            cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color}
            strokeWidth={strokeWidth} strokeDasharray={circumference} strokeDashoffset={strokeDashoffset}
            strokeLinecap="round" className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          {Icon && <Icon className="w-4 h-4" style={{ color }} />}
        </div>
      </div>
      <p className="mt-1 text-lg font-bold text-[#0F4D40]">{Math.round(max - value)}g</p>
    </div>
  );
};

const AthleteDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("tracking");
  const [mealPlan, setMealPlan] = useState(defaultMealPlan);
  const [completedMeals, setCompletedMeals] = useState({});
  const [selectedDayIndex, setSelectedDayIndex] = useState(3);
  const [showConfetti, setShowConfetti] = useState(false);
  const [currentStreak, setCurrentStreak] = useState(14);
  const [isEditingPlan, setIsEditingPlan] = useState(false);
  const [isUploadingPlan, setIsUploadingPlan] = useState(false);

  // Generate week days
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() + i - 3);
    return {
      date,
      dayName: date.toLocaleDateString('es-ES', { weekday: 'short' }).charAt(0).toUpperCase(),
      dayNumber: date.getDate(),
      isToday: i === 3
    };
  });

  useEffect(() => {
    // Check if logged in as athlete
    const athleteToken = localStorage.getItem("habitia_athlete_token");
    if (!athleteToken) {
      navigate("/athlete/login");
      return;
    }
    setTimeout(() => setLoading(false), 500);
  }, [navigate]);

  const toggleMealComplete = (mealKey) => {
    setCompletedMeals(prev => {
      const newState = { ...prev, [mealKey]: !prev[mealKey] };
      
      // Check if all meals completed
      const allCompleted = Object.keys(mealPlan.meals).every(key => newState[key]);
      if (allCompleted) {
        setShowConfetti(true);
        setCurrentStreak(prev => prev + 1);
        setTimeout(() => setShowConfetti(false), 3000);
      }
      
      return newState;
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingPlan(true);
    
    // Simulate processing
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    toast.success("¡Plan de dieta procesado exitosamente!");
    setIsUploadingPlan(false);
  };

  const calculateConsumed = () => {
    return Object.entries(completedMeals).reduce((acc, [key, completed]) => {
      if (completed && mealPlan.meals[key]) {
        const meal = mealPlan.meals[key];
        return {
          calories: acc.calories + meal.calories,
          protein: acc.protein + meal.protein,
          carbs: acc.carbs + meal.carbs,
          fat: acc.fat + meal.fat
        };
      }
      return acc;
    }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
  };

  const consumed = calculateConsumed();
  const caloriesLeft = mealPlan.calories - consumed.calories;

  const handleLogout = () => {
    localStorage.removeItem("habitia_athlete_token");
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F4D40] to-[#134E3E] flex items-center justify-center">
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-white animate-pulse" />
          </div>
          <p className="text-white/80 font-medium">Cargando tu plan...</p>
        </motion.div>
      </div>
    );
  }

  const renderTrackingTab = () => (
    <div className="space-y-4">
      {/* Day Selector */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => setSelectedDayIndex(Math.max(0, selectedDayIndex - 1))}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          
          <div className="flex gap-2">
            {weekDays.map((day, index) => (
              <button
                key={index}
                onClick={() => setSelectedDayIndex(index)}
                className={`flex flex-col items-center p-2 rounded-xl transition-all ${
                  selectedDayIndex === index 
                    ? 'bg-[#0F4D40] text-white' 
                    : day.isToday 
                      ? 'bg-[#178A6B]/10 text-[#0F4D40]' 
                      : 'hover:bg-gray-50'
                }`}
              >
                <span className={`text-xs font-medium ${selectedDayIndex === index ? 'text-white/70' : 'text-gray-400'}`}>
                  {day.dayName}
                </span>
                <span className={`text-lg font-bold ${selectedDayIndex === index ? 'text-white' : 'text-gray-700'}`}>
                  {day.dayNumber}
                </span>
              </button>
            ))}
          </div>
          
          <button 
            onClick={() => setSelectedDayIndex(Math.min(weekDays.length - 1, selectedDayIndex + 1))}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Calories Overview */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <motion.p 
              key={caloriesLeft}
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              className="text-5xl font-bold text-[#0F4D40]"
            >
              {Math.max(0, caloriesLeft)}
            </motion.p>
            <p className="text-gray-500 mt-1">Calorías restantes</p>
          </div>
          <div className="relative w-24 h-24">
            <svg className="w-full h-full -rotate-90">
              <circle cx="48" cy="48" r="40" fill="none" stroke="#E5E7EB" strokeWidth="12" />
              <motion.circle
                cx="48" cy="48" r="40" fill="none" stroke="#0F4D40" strokeWidth="12"
                strokeLinecap="round" strokeDasharray={251}
                initial={{ strokeDashoffset: 251 }}
                animate={{ strokeDashoffset: 251 - (consumed.calories / mealPlan.calories) * 251 }}
                transition={{ duration: 0.5 }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <Apple className="w-6 h-6 text-[#0F4D40]" />
            </div>
          </div>
        </div>

        {/* Macros */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <CircularProgress value={consumed.protein} max={mealPlan.protein} color="#DC2626" icon={Beef} />
          <CircularProgress value={consumed.carbs} max={mealPlan.carbs} color="#F59E0B" icon={Wheat} />
          <CircularProgress value={consumed.fat} max={mealPlan.fat} color="#3B82F6" icon={Droplet} />
        </div>
        <div className="grid grid-cols-3 gap-4 mt-2 text-center text-xs text-gray-500">
          <span>Proteína</span>
          <span>Carbos</span>
          <span>Grasas</span>
        </div>
      </div>

      {/* Meals */}
      <div className="space-y-3">
        <div className="flex items-center justify-between px-1">
          <h3 className="font-bold text-[#0F4D40]">Mis comidas</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setActiveTab("plan")}
            className="text-[#178A6B] text-xs"
          >
            <Edit className="w-3 h-3 mr-1" />
            Editar plan
          </Button>
        </div>
        
        {Object.entries(mealPlan.meals).map(([key, meal]) => (
          <motion.div
            key={key}
            layout
            className={`bg-white rounded-2xl p-4 shadow-sm border transition-all ${
              completedMeals[key] 
                ? 'border-[#178A6B] bg-[#178A6B]/5' 
                : 'border-gray-100'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-[#0F4D40]">{meal.name}</h4>
                  <span className="text-xs text-gray-400">{meal.time}</span>
                </div>
                <p className="text-sm text-gray-500 mb-2">{meal.foods.join(' • ')}</p>
                <div className="flex items-center gap-3 text-xs">
                  <span className="font-medium text-[#0F4D40]">{meal.calories} kcal</span>
                  <span className="text-gray-400">|</span>
                  <span className="text-red-500">P: {meal.protein}g</span>
                  <span className="text-amber-500">C: {meal.carbs}g</span>
                  <span className="text-blue-500">G: {meal.fat}g</span>
                </div>
              </div>
              
              <button
                onClick={() => toggleMealComplete(key)}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  completedMeals[key] 
                    ? 'bg-[#178A6B] text-white' 
                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                }`}
              >
                <Check className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Streak */}
      <div className="bg-gradient-to-r from-[#E57A4F]/10 to-[#F59E0B]/10 border border-[#E57A4F]/20 rounded-2xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-[#0F4D40]">Mi racha</p>
            <p className="text-sm text-gray-500">¡Sigue así!</p>
          </div>
          <div className="flex items-center gap-2">
            <Flame className="w-6 h-6 text-[#E57A4F]" />
            <span className="text-3xl font-bold text-[#0F4D40]">{currentStreak}</span>
            <span className="text-gray-500">días</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPlanTab = () => (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <h2 className="text-xl font-bold text-[#0F4D40]">Mi Plan 📋</h2>
        <p className="text-gray-500 text-sm">Configura tu dieta personalizada</p>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <h3 className="font-semibold text-[#0F4D40] mb-3 flex items-center gap-2">
          <Upload className="w-5 h-5" />
          Subir Plan de Dieta
        </h3>
        
        <label className={`block border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
          isUploadingPlan ? 'border-[#178A6B] bg-[#178A6B]/5' : 'border-gray-200 hover:border-[#178A6B]'
        }`}>
          <input
            type="file"
            onChange={handleFileUpload}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            className="hidden"
          />
          
          {isUploadingPlan ? (
            <div className="space-y-2">
              <div className="w-10 h-10 border-3 border-[#178A6B] border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-[#178A6B] font-medium text-sm">Procesando...</p>
            </div>
          ) : (
            <>
              <File className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-600 font-medium text-sm">Sube tu dieta</p>
              <p className="text-xs text-gray-400">PDF, Word o imagen</p>
            </>
          )}
        </label>
      </div>

      {/* Daily Goals */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-[#0F4D40]">Metas Diarias</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditingPlan(!isEditingPlan)}
            className="text-[#178A6B]"
          >
            {isEditingPlan ? <Save className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
          </Button>
        </div>
        
        <div className="space-y-3">
          {[
            { label: "Calorías", value: mealPlan.calories, unit: "kcal", color: "text-[#0F4D40]" },
            { label: "Proteína", value: mealPlan.protein, unit: "g", color: "text-red-500" },
            { label: "Carbohidratos", value: mealPlan.carbs, unit: "g", color: "text-amber-500" },
            { label: "Grasas", value: mealPlan.fat, unit: "g", color: "text-blue-500" },
          ].map((item) => (
            <div key={item.label} className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
              <span className="text-gray-600">{item.label}</span>
              {isEditingPlan ? (
                <Input
                  type="number"
                  defaultValue={item.value}
                  className="w-24 h-8 text-right"
                />
              ) : (
                <span className={`font-bold ${item.color}`}>{item.value} {item.unit}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Edit Meals */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-[#0F4D40]">Mis Comidas</h3>
          <Button variant="ghost" size="sm" className="text-[#178A6B]">
            <Plus className="w-4 h-4 mr-1" />
            Agregar
          </Button>
        </div>
        
        <div className="space-y-3">
          {Object.entries(mealPlan.meals).map(([key, meal]) => (
            <div key={key} className="p-3 bg-gray-50 rounded-xl">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-[#0F4D40]">{meal.name}</span>
                <span className="text-sm text-[#178A6B]">{meal.calories} kcal</span>
              </div>
              <p className="text-xs text-gray-500">{meal.foods.join(' • ')}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderProgressTab = () => (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <h2 className="text-xl font-bold text-[#0F4D40]">Mi Progreso 📈</h2>
        <p className="text-gray-500 text-sm">Tu historial de adherencia</p>
      </div>

      <div className="bg-gradient-to-br from-[#E57A4F]/15 to-[#F59E0B]/15 border border-[#E57A4F]/30 rounded-3xl p-6 text-center">
        <div className="flex items-center justify-center gap-4 mb-2">
          <Flame className="w-10 h-10 text-[#E57A4F]" />
          <span className="text-5xl font-bold text-[#0F4D40]">{currentStreak}</span>
        </div>
        <p className="text-gray-600 font-semibold">días consecutivos</p>
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <h3 className="font-semibold text-[#0F4D40] mb-4">Últimos 30 días</h3>
        <div className="grid grid-cols-10 gap-1.5">
          {Array.from({ length: 30 }, (_, i) => (
            <div
              key={i}
              className={`w-full aspect-square rounded-md ${
                i >= 16 ? 'bg-[#178A6B]' : 'bg-gray-100'
              }`}
            />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
          <p className="text-2xl font-bold text-[#0F4D40]">38</p>
          <p className="text-xs text-gray-500">Check-ins</p>
        </div>
        <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
          <p className="text-2xl font-bold text-[#178A6B]">92%</p>
          <p className="text-xs text-gray-500">Adherencia</p>
        </div>
        <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
          <p className="text-2xl font-bold text-[#E57A4F]">-4kg</p>
          <p className="text-xs text-gray-500">Progreso</p>
        </div>
      </div>
    </div>
  );

  const renderSettingsTab = () => (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <h2 className="text-xl font-bold text-[#0F4D40]">Configuración ⚙️</h2>
        <p className="text-gray-500 text-sm">Personaliza tu experiencia</p>
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <h3 className="font-semibold text-[#0F4D40] mb-4">Mi Objetivo</h3>
        <Input
          defaultValue="Ganar masa muscular y mantener grasa baja"
          className="rounded-xl"
        />
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <h3 className="font-semibold text-[#0F4D40] mb-4">Recordatorios</h3>
        <div className="space-y-3">
          {[
            { label: "Check-in diario", time: "09:00", enabled: true },
            { label: "Recordatorio de comidas", time: "Cada 3h", enabled: true },
            { label: "Resumen semanal", time: "Domingos", enabled: false },
          ].map((item) => (
            <div key={item.label} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div>
                <p className="font-medium text-gray-700">{item.label}</p>
                <p className="text-xs text-gray-500">{item.time}</p>
              </div>
              <Button variant={item.enabled ? "default" : "outline"} size="sm" className="rounded-full">
                {item.enabled ? "On" : "Off"}
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <h3 className="font-semibold text-[#0F4D40] mb-4">Mi Plan</h3>
        <div className="flex items-center justify-between p-3 bg-[#178A6B]/10 rounded-xl border border-[#178A6B]/20">
          <div>
            <p className="font-medium text-[#0F4D40]">Athlete Pro</p>
            <p className="text-xs text-gray-500">$19/mes • IA + Análisis</p>
          </div>
          <Button variant="outline" size="sm" className="rounded-full border-[#178A6B] text-[#178A6B]">
            Cambiar
          </Button>
        </div>
      </div>

      <Button
        variant="outline"
        className="w-full rounded-xl border-red-200 text-red-500 hover:bg-red-50"
        onClick={handleLogout}
      >
        <LogOut className="w-4 h-4 mr-2" />
        Cerrar sesión
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24">
      {showConfetti && <Confetti recycle={false} numberOfPieces={200} colors={['#178A6B', '#E57A4F', '#F59E0B', '#0F4D40']} />}
      
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-4 py-3 sticky top-0 z-40 shadow-sm">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#0F4D40] to-[#178A6B] rounded-xl flex items-center justify-center shadow-md">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">HabitIA</p>
              <p className="font-bold text-[#0F4D40] text-sm">Mi Coach Personal</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 bg-gradient-to-r from-[#E57A4F]/10 to-[#F59E0B]/10 px-3 py-2 rounded-full border border-[#E57A4F]/20">
            <Flame className="w-5 h-5 text-[#E57A4F]" />
            <span className="font-bold text-[#0F4D40] text-lg">{currentStreak}</span>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-md mx-auto px-4 py-4">
        {activeTab === "tracking" && renderTrackingTab()}
        {activeTab === "plan" && renderPlanTab()}
        {activeTab === "progress" && renderProgressTab()}
        {activeTab === "settings" && renderSettingsTab()}
      </main>

      {/* Bottom navigation */}
      <nav className="fixed bottom-0 left-0 right-0 h-20 bg-white border-t border-gray-100 shadow-lg z-[99999]">
        <div className="max-w-md mx-auto grid grid-cols-4 h-full">
          {[
            { key: "tracking", icon: CheckCircle, label: "Tracking" },
            { key: "plan", icon: FileText, label: "Mi Plan" },
            { key: "progress", icon: TrendingUp, label: "Progreso" },
            { key: "settings", icon: Settings, label: "Config" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex flex-col items-center justify-center gap-1 transition-all ${
                activeTab === tab.key ? "text-[#0F4D40]" : "text-gray-400"
              }`}
            >
              <div className={`p-2 rounded-xl ${activeTab === tab.key ? 'bg-[#178A6B]/10' : ''}`}>
                <tab.icon className={`w-5 h-5 ${activeTab === tab.key ? 'text-[#178A6B]' : ''}`} />
              </div>
              <span className={`text-xs font-medium ${activeTab === tab.key ? 'text-[#0F4D40]' : ''}`}>
                {tab.label}
              </span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default AthleteDashboard;
